import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { LoginService } from 'src/app/core/services/login.service';
import { environment } from 'src/environments/environment';
import { DialogExpirationComponent } from '../components/dialog-expiration/dialog-expiration.component';
import { OpenRoutes } from '../routes/open-routes';
import { PanelRoutes } from '../routes/panel-routes';
import { IdentityService } from '../services/identity.service';
import { NotificationService } from '../services/notification.service';

@Injectable()
export class AuthGuard implements CanActivate {
  private readonly TIME_TO_EXPIRE: number = environment.sessionExpirationTime;

  idleState = 'Not started.';
  timedOut = false;
  lastPing?: Date = null;
  title = 'angular-idle-timeout';
  dialogExpiration: MatDialogRef<DialogExpirationComponent>;
  count: number = 0;
  isOpen: boolean = false;

  constructor(
    private loginService: LoginService,
    private identityService: IdentityService,
    private idle: Idle,
    private keepalive: Keepalive,
    private router: Router,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private dialog: MatDialog
  ) {
    this.router.events.subscribe(() => (this.isOpen = false));
  }

  sessionExpiration() {
    if (localStorage.getItem('user')) {
      // sets an idle timeout of 5 seconds, for testing purposes.
      this.idle.setIdle(this.TIME_TO_EXPIRE);
      // sets a timeout period of 5 seconds. after 10 seconds of inactivity, the user will be considered timed out.
      this.idle.setTimeout(10);
      // sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
      this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

      this.idle.onIdleEnd.subscribe(() => {
        this.idleState = 'No longer idle.';
        this.reset();
      });

      this.idle.onTimeout.subscribe(() => {
        this.idleState = 'Timed out!';
        this.timedOut = true;
        this.identityService.clearSession();
        this.dialogExpiration.close();
        this.notificationService.showWarning('Vuelve a iniciar sesión', 'Sesión expirada');
        this.router.navigate(['/login']);
        return;
      });

      this.idle.onIdleStart.subscribe(() => {
        this.idleState = "You've gone idle!";
        console.log('this.isOpen:', this.isOpen);

        if (!this.isOpen) {
          this.isOpen = true;
          this.dialogExpiration = this.dialog.open(DialogExpirationComponent, {
            data: {
              confirmation: this.count
            }
          });
        }
        this.dialogExpiration.afterClosed().subscribe((response) => {
          if (response.result == 'close') {
            this.idle.stop();
          }

          if (typeof response.result === 'boolean') {
            return (this.isOpen = false);
          } else {
            return true;
          }
        });
      });

      this.idle.onTimeoutWarning.subscribe((countdown) => {
        this.count = countdown;
        this.idleState = 'You will time out in ' + countdown + ' seconds!';
      });

      // sets the ping interval to 15 seconds
      this.keepalive.interval(15);

      this.keepalive.onPing.subscribe(() => (this.lastPing = new Date()));

      this.reset();
    } else {
      return false;
    }
  }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    const user = this.loginService.getCurrentLogged();

    if (!user && state.url.endsWith(OpenRoutes.login)) {
      this.loginService.clearSessionStorage();
      return true;
    }

    if (
      (!user && state.url.endsWith(OpenRoutes.register)) ||
      state.url.endsWith(OpenRoutes.restore) ||
      state.url.startsWith(OpenRoutes.recover)
    ) {
      this.loginService.clearSessionStorage();
      return true;
    }

    const token = user && user.token ? user.token : null;
    if (token && (state.url.endsWith(OpenRoutes.login) || state.url.endsWith(OpenRoutes.register))) {
      if (user.organismo && !user.organismo.rubro) {
        this.router.navigate(['/user/my-commerce']);
      } else if (this.identityService.isRolResponsible()) {
        this.router.navigate(['/user/movements/'], { queryParams: { page: '1' } });
      } else {
        this.router.navigate(['/user/account-yacare']);
      }

      return false;
    }

    if (token) {
      this.sessionExpiration();
    }

    const necessaryPermission = next.data['permission'];
    if (necessaryPermission && !this.identityService.hasPermission(necessaryPermission)) {
      let hasPermission = false;

      if (Array.isArray(necessaryPermission)) {
        hasPermission = necessaryPermission.some((permission) => this.identityService.hasPermission(permission));
      } else {
        hasPermission = this.identityService.hasPermission(necessaryPermission);
      }

      if (!hasPermission) {
        this.notificationService.showWarning('No tenés permiso para acceder a esta sección', 'Acceso denegado');
        this.router.navigate(['/user/help']);
        return false;
      }
    }

    if (
      token &&
      (!state.url.endsWith(OpenRoutes.login) ||
        !state.url.endsWith(OpenRoutes.recover) ||
        !state.url.endsWith(OpenRoutes.register))
    ) {
      //comerce only url
      if (user.organismo && !user.organismo.rubro && !state.url.endsWith(PanelRoutes.panel.profile)) {
        return false;
      }

      const current = this.loginService.getCurrentLogged();
      const isResponsible = current.isResponsible != null ? current.isResponsible : false;

      if (isResponsible) {
        if (state.url.endsWith(PanelRoutes.panel.profile)) {
          this.router.navigate(['user/cashin-cashout']);
          return !isResponsible;
        } else if (state.url.endsWith(PanelRoutes.panel.cashYacare)) {
          this.router.navigate(['user/cashin-cashout']);
          return !isResponsible;
        } else if (state.url.endsWith(PanelRoutes.panel.transfer)) {
          this.router.navigate(['user/cashin-cashout']);
          return !isResponsible;
        } else {
          return true;
        }
      } else {
        return true;
      }
    }

    return false;
  }

  translate(key: string): string {
    return this.translateService.instant(key);
  }

  reset() {
    this.idle.watch();
    this.idleState = 'Started.';
    this.timedOut = false;
  }
}
